import React from "react";
import { YStack, XStack, SizableText, H3, H4, Card, Separator, Button, Spinner } from "tamagui";
import apis from "@api/api";
import { useQuery } from "@tanstack/react-query";
import * as Animatable from 'react-native-animatable';
import { View, Dimensions } from "react-native";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import toTitleCase from "@hooks/toTitleCase";

const StripeOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["stripeProfile"],
    queryFn: async () => await apis.getStripeProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const activeCapabilities = (capabilities = {}) =>
    Object.entries(capabilities)
      .filter(([, status]) => status === "active")
      .map(([key]) => toTitleCase(key.replace(/_/g, " ")));

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load Stripe profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your Stripe account, or try removing and re-adding the integration.  
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#635BFF" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading Stripe account...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {data && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header */}
                <XStack alignItems="center" justifyContent="space-between" marginBottom="$4">
                  <H3
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="700"
                    flex={1}
                  >
                    {data.settings?.dashboard?.display_name || 'Stripe Account'}
                  </H3>
                  {data.dashboard_url && (
                    <Button
                      backgroundColor="#635BFF"
                      color="white"
                      size="$3"
                      onPress={() => {
                        if (typeof window !== 'undefined' && data.dashboard_url) {
                          window.open(data.dashboard_url, "_blank");
                        }
                      }}
                      borderRadius={12}
                      paddingHorizontal="$4"
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons name="dashboard" size={16} color="white" />
                        <SizableText color="white" fontWeight="600" size="$3">
                          Dashboard
                        </SizableText>
                      </XStack>
                    </Button>
                  )}
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Account Details */}
                <YStack gap="$3">
                  <DetailRow
                    icon="badge"
                    label="Account ID"
                    value={data.id}
                    theme={theme}
                  />
                  <DetailRow
                    icon="public"
                    label="Country"
                    value={data.country}
                    theme={theme}
                  />
                  <DetailRow
                    icon="attach-money"
                    label="Currency"
                    value={data.default_currency?.toUpperCase()}
                    theme={theme}
                  />
                  <DetailRow
                    icon="business"
                    label="Business Type"
                    value={toTitleCase(data.business_type || '')}
                    theme={theme}
                  />

                  {/* Status Indicators */}
                  <XStack gap="$3" marginTop="$2">
                    <StatusBadge
                      label="Charges"
                      enabled={data.charges_enabled}
                      theme={theme}
                    />
                    <StatusBadge
                      label="Payouts"
                      enabled={data.payouts_enabled}
                      theme={theme}
                    />
                    <StatusBadge
                      label="Details"
                      enabled={data.details_submitted}
                      theme={theme}
                    />
                  </XStack>

                  {data.settings?.branding?.primary_color && (
                    <XStack alignItems="center" gap="$3" marginTop="$2">
                      <View style={{
                        backgroundColor: theme ? "#f3e8ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="palette" size={20} color="#635BFF" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Brand Color
                        </SizableText>
                        <XStack alignItems="center" gap="$2">
                          <View
                            style={{
                              width: 24,
                              height: 24,
                              backgroundColor: data.settings.branding.primary_color,
                              borderRadius: 6,
                              borderWidth: 1,
                              borderColor: theme ? "#e2e8f0" : "#334155"
                            }}
                          />
                          <SizableText
                            size="$4"
                            color={theme ? "#1e293b" : "#f1f5f9"}
                            fontWeight="600"
                          >
                            {data.settings.branding.primary_color}
                          </SizableText>
                        </XStack>
                      </YStack>
                    </XStack>
                  )}

                  {data.settings?.payouts?.schedule && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#f3e8ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="schedule" size={20} color="#635BFF" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Payout Schedule
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {data.settings.payouts.schedule.interval} (Delay: {data.settings.payouts.schedule.delay_days} days)
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {activeCapabilities(data.capabilities).length > 0 && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#f3e8ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="verified" size={20} color="#635BFF" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Active Capabilities
                        </SizableText>
                        <SizableText
                          size="$3"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {activeCapabilities(data.capabilities).join(", ")}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}
                </YStack>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    Stripe Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    Your Stripe account is fully integrated. You can now accept payments and manage transactions directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

const DetailRow = ({ icon, label, value, theme }: { icon: string; label: string; value: string; theme: boolean }) => (
  <XStack alignItems="center" gap="$3">
    <View style={{
      backgroundColor: theme ? "#f3e8ff" : "#581c87",
      borderRadius: 8,
      padding: 8
    }}>
      <MaterialIcons name={icon as any} size={20} color="#635BFF" />
    </View>
    <YStack flex={1}>
      <SizableText
        size="$2"
        color={theme ? "#64748b" : "#94a3b8"}
      >
        {label}
      </SizableText>
      <SizableText
        size="$4"
        color={theme ? "#1e293b" : "#f1f5f9"}
        fontWeight="600"
      >
        {value}
      </SizableText>
    </YStack>
  </XStack>
);

const StatusBadge = ({ label, enabled, theme }: { label: string; enabled: boolean; theme: boolean }) => (
  <Card
    backgroundColor={enabled ? (theme ? "#d1fae5" : "#065f46") : (theme ? "#fee2e2" : "#7f1d1d")}
    borderRadius={8}
    padding="$2"
    flex={1}
  >
    <XStack alignItems="center" gap="$1" justifyContent="center">
      <MaterialIcons
        name={enabled ? "check-circle" : "cancel"}
        size={14}
        color={enabled ? "#10b981" : "#ef4444"}
      />
      <SizableText
        size="$2"
        color={enabled ? "#10b981" : "#ef4444"}
        fontWeight="600"
      >
        {label}
      </SizableText>
    </XStack>
  </Card>
);

export default StripeOptions;
